import React, { useEffect, useState, useMemo } from "react";
import DelBox from "../comps/DelBox";
import AddNurse from "../models/AddNurse";
import AppConst from "../apis/appconst";
import axios from "axios";
import { toast } from "react-toastify";
import DataTable from 'react-data-table-component';

const Nurses = () => {
  const [formData, setFormData] = useState({
    l1: [],
    cat_type: "nurse",
    search_key: "",
    page_no: 0,
    alertMsg: "",
    alertMsgTyp: "",
    isAlert: false,
  });

  const [isAdmin, setIsAdmin] = useState(false);
  const [ldata, setLdata] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [loading, setLoading] = useState(false);

  const initData = async (pageNo = formData.page_no, searchKey = formData.search_key) => {
    const storedData = sessionStorage.getItem("ldata");
    if (!storedData) {
      setFormData((prev) => ({ ...prev, l1: [] }));
      return;
    }

    const userData = JSON.parse(storedData);
    const roleId = userData?.role_id;
    setIsAdmin(roleId === 1 || roleId === "1");

    setLoading(true);
    const fd = new FormData();
    fd.append("type", (formData.cat_type || "nurse") + "");
    fd.append("search_key", (searchKey || "") + "");
    fd.append("page_no", (pageNo || 0) + "");
    fd.append("user_cre_by", 1 + "");

    try {
      const response = await axios.post(AppConst.BASE_URL + AppConst.getUserListUrl, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response?.data?.status) {
        const records = response.data.records || [];
        setFormData((prev) => ({ ...prev, l1: Array.isArray(records) ? records : [] }));
      } else {
        setFormData((prev) => ({ ...prev, l1: [] }));
      }
    } catch (error) {
      console.error("Error fetching nurses:", error);
      toast.error("Failed to load nurses");
      setFormData((prev) => ({ ...prev, l1: [] }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const storedData = sessionStorage.getItem("ldata");
    if (storedData) {
      setLdata(JSON.parse(storedData));
    }
    initData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const delItem = async (item) => {
    if (!item?.user_id) return;
    
    if (!window.confirm('Are you sure you want to delete this nurse?')) {
      return;
    }
    
    const fd = new FormData();
    fd.append("user_id", item.user_id + "");
    
    try {
      const response = await axios.post(AppConst.BASE_URL + AppConst.delUserUrl, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      if (response?.data?.status) {
        await initData();
        toast.success("Deleted successfully");
      } else {
        toast.error("Delete failed");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Delete failed");
    }
  };

  const handleAddDepart = () => {
    setFormData((prev) => ({ ...prev, page_no: 0 }));
    initData(0, formData.search_key);
  };

  // ✅ Filter data based on search
  const filteredList = useMemo(() => {
    if ((formData.search_key || "").trim() === "") {
      return formData.l1 || [];
    }
    return (formData.l1 || []).filter((item) => {
      const rag = item?.doc?.nurse_rag_number ? `${item.doc.nurse_rag_number}` : "";
      const name = item?.fname ? `${item.fname}` : "";
      const mobile = item?.mobile ? `${item.mobile}` : "";
      const department = item?.doc?.nurse_depart ? `${item.doc.nurse_depart}` : "";
      const q = formData.search_key.toLowerCase();
      
      return (
        rag.toLowerCase().includes(q) || 
        name.toLowerCase().includes(q) ||
        mobile.toLowerCase().includes(q) ||
        department.toLowerCase().includes(q)
      );
    });
  }, [formData.l1, formData.search_key]);

  // Safe helper to render fields
  const safe = (val) => (val || val === 0 ? val : "-");

  // ✅ Define DataTable columns
  const columns = [
    {
      name: 'Reg. No.',
      selector: row => row?.doc?.nurse_rag_number || '-',
      sortable: true,
      width: '120px',
      cell: row => (
        <span className="badge bg-primary-subtle text-primary">
          {safe(row?.doc?.nurse_rag_number)}
        </span>
      ),
    },
    {
      name: 'Name',
      selector: row => row?.fname || '-',
      sortable: true,
      grow: 2,
      cell: row => <strong>{safe(row?.fname)}</strong>,
    },
    {
      name: 'Address',
      selector: row => {
        const parts = [
          row?.address_line1,
          row?.address_line2,
          row?.city,
          row?.pincode,
        ].filter(Boolean);
        return parts.length > 0 ? parts.join(", ") : "-";
      },
      sortable: true,
      grow: 2,
      wrap: true,
      cell: row => {
        const parts = [
          row?.address_line1,
          row?.address_line2,
          row?.city,
          row?.pincode,
        ].filter(Boolean);
        const address = parts.length > 0 ? parts.join(", ") : "-";
        return <span className="text-muted small">{address}</span>;
      },
    },
    {
      name: 'Phone Number',
      selector: row => row?.mobile || '-',
      sortable: true,
      width: '130px',
      cell: row => (
        <span>
          <i className="ri-phone-line me-1 text-muted"></i>
          {safe(row?.mobile)}
        </span>
      ),
    },
    {
      name: 'Department',
      selector: row => row?.doc?.nurse_depart || '-',
      sortable: true,
      width: '150px',
      cell: row => (
        <span className="badge bg-info-subtle text-info">
          {safe(row?.doc?.nurse_depart)}
        </span>
      ),
    },
    {
      name: 'Type',
      selector: row => row?.doc?.nurse_type || '-',
      sortable: true,
      width: '120px',
      cell: row => (
        <span className="badge bg-success-subtle text-success">
          {safe(row?.doc?.nurse_type)}
        </span>
      ),
    },
    {
      name: 'Action',
      center: true,
      width: '200px',
      cell: row => (
        <div className="d-flex gap-2">
          {/* EDIT BUTTON */}
          <button
            type="button"
            className="btn btn-sm btn-outline-primary d-inline-flex align-items-center"
            disabled={!isAdmin}
            style={{ opacity: !isAdmin ? 0.5 : 1 }}
            onClick={(e) => {
              e.preventDefault();
              setEditItem(row);
              const el = document.getElementById("addbt453");
              if (el) el.click();
            }}
            title="Edit Nurse"
          >
            <i className="ri-pencil-fill fs-16 me-1"></i> Edit
          </button>

          {/* DELETE BUTTON */}
          <button
            type="button"
            className="btn btn-sm btn-outline-danger d-inline-flex align-items-center"
            disabled={!isAdmin}
            style={{ opacity: !isAdmin ? 0.5 : 1 }}
            onClick={(e) => {
              e.preventDefault();
              delItem(row);
            }}
            title="Delete Nurse"
          >
            <i className="ri-delete-bin-5-fill fs-16 me-1"></i> Delete
          </button>
        </div>
      ),
    },
  ];

  // ✅ Custom styles for DataTable
  const customStyles = {
    headRow: {
      style: {
        backgroundColor: '#f3f6f9',
        borderBottom: '2px solid #e9ecef',
        minHeight: '48px',
      },
    },
    headCells: {
      style: {
        fontSize: '13px',
        fontWeight: '600',
        textTransform: 'uppercase',
        color: '#878a99',
        paddingLeft: '16px',
        paddingRight: '16px',
      },
    },
    rows: {
      style: {
        minHeight: '56px',
        '&:hover': {
          backgroundColor: '#f8f9fa',
        },
      },
    },
    cells: {
      style: {
        paddingLeft: '16px',
        paddingRight: '16px',
        fontSize: '14px',
      },
    },
  };

  // ✅ Pagination options
  const paginationComponentOptions = {
    rowsPerPageText: 'Rows per page:',
    rangeSeparatorText: 'of',
    selectAllRowsItem: true,
    selectAllRowsItemText: 'All',
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="row">
          <div className="col-lg-12">
            <div className="card" id="orderList">
              {/* HEADER */}
              <div className="card-header border-0">
                <div className="row align-items-center gy-3">
                  <div className="col-sm">
                    <h5 className="card-title mb-0">Nurses</h5>
                  </div>
                  <div className="col-sm-auto">
                    <div className="d-flex gap-1 flex-wrap">
                      <button
                        id="addbt453"
                        style={{ display: "none" }}
                        type="button"
                        className="btn btn-success add-btn"
                        data-bs-toggle="modal"
                        data-bs-target="#showModal"
                      />
                      <button
                        type="button"
                        className="btn btn-success add-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          setEditItem(null);
                          const el = document.getElementById("addbt453");
                          if (el) el.click();
                        }}
                      >
                        <i className="ri-add-line align-bottom me-1"></i> Add Nurse
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* SEARCH BOX */}
              <div className="card-body border border-dashed border-end-0 border-start-0">
                <form onSubmit={(e) => e.preventDefault()}>
                  <div className="row g-3">
                    <div className="col-xxl-5 col-sm-6">
                      <div className="search-box">
                        <input
                          className="form-control search"
                          placeholder="Search by reg. no, name, phone or department..."
                          type="text"
                          name="search_key"
                          onChange={handleChange}
                          value={formData.search_key}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </div>
                    {formData.search_key && (
                      <div className="col-auto">
                        <button
                          type="button"
                          className="btn btn-soft-secondary"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, search_key: '' }));
                          }}
                        >
                          <i className="ri-close-line me-1"></i>
                          Clear Search
                        </button>
                      </div>
                    )}
                  </div>
                </form>
              </div>

              {/* DATA TABLE */}
              <div className="card-body pt-0">
                <DataTable
                  columns={columns}
                  data={filteredList}
                  pagination
                  paginationPerPage={10}
                  paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 50, 100]}
                  paginationComponentOptions={paginationComponentOptions}
                  highlightOnHover
                  striped
                  responsive
                  customStyles={customStyles}
                  progressPending={loading}
                  progressComponent={
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  }
                  noDataComponent={
                    <div className="text-center py-5">
                      <i className="ri-nurse-line" style={{ fontSize: '48px', color: '#ccc' }}></i>
                      <h5 className="mt-3">No Nurses Found</h5>
                      <p className="text-muted">
                        {formData.search_key 
                          ? `No results found for "${formData.search_key}". Try a different search.`
                          : "No nurses have been added yet. Click 'Add Nurse' to create one."
                        }
                      </p>
                    </div>
                  }
                />
              </div>
            </div>
          </div>
        </div>

        {/* MODALS */}
        <AddNurse item={editItem} onAddDepart={handleAddDepart} />
        <DelBox />
      </div>
    </div>
  );
};

export default Nurses;